import { useEffect, useRef } from 'react';
import { Button } from '../../components/ui/button';

// Opt-out confirmation (P2b-2 §6.7.5). Fires from the master-grid actions
// menu when AM clicks "Mark as Opted Out". Irreversible-feeling action so
// the copy is explicit about what gets cleared and what survives. Default
// focus is Cancel -- if the AM hits Enter by accident we do nothing rather
// than wipe the row. Matches BudgetDriftModal's centre-overlay convention.

type Props = {
  locationName: string;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
};

export function OptOutConfirmModal({ locationName, onConfirm, onCancel, busy }: Props) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Focus Cancel on mount so Enter doesn't accidentally confirm a
    // destructive operation.
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div
        className="w-full max-w-lg rounded-md bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="opt-out-title"
      >
        <div className="p-6 space-y-4">
          <h2 id="opt-out-title" className="text-lg font-semibold text-slate-900">
            Mark {locationName} as Opted Out?
          </h2>
          <p className="text-sm text-slate-700">
            This will clear all content, budgets, dates, ready flags, and execution
            state on this row. Only <strong>Notes</strong> will be preserved.
          </p>
          <p className="text-sm text-slate-600">
            The row will remain visible on the rollout for historical reference. You
            can reverse this later.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <button
              ref={cancelRef}
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <Button onClick={onConfirm} disabled={busy}>
              {busy ? 'Marking as Opted Out…' : 'Mark as Opted Out'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
